@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)

package com.example.monetix.ui.dialogs

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.rounded.ChevronLeft
import androidx.compose.material.icons.rounded.ChevronRight
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.example.monetix.db.TransactionEntity
import com.example.monetix.db.CategoryEntity
import com.example.monetix.db.AccountEntity
import com.example.monetix.utils.FileExporter
import com.example.monetix.utils.ExportMode
import kotlinx.datetime.*

@Composable
fun ExportPdfDialog(
    transactions: List<TransactionEntity>,
    categories: List<CategoryEntity>,
    accounts: List<AccountEntity>,
    includeHidden: Boolean,
    availableBalanceCents: Long,
    exporter: FileExporter,
    onDismiss: () -> Unit
) {
    val today = remember { Clock.System.todayIn(TimeZone.currentSystemDefault()) }
    val mesiItaliani = listOf("Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre")

    var selectedPeriod by remember { mutableStateOf("MESE") }
    var selectedType by remember { mutableStateOf("TUTTO") }
    var selectedCategory by remember { mutableStateOf<CategoryEntity?>(null) }
    var selectedAccountId by remember { mutableStateOf<String?>(null) }
    var merchantQuery by remember { mutableStateOf("") }
    var noteQuery by remember { mutableStateOf("") }

    var targetMonth by remember { mutableStateOf(today.monthNumber) }
    var targetYear by remember { mutableStateOf(today.year) }

    var selectedExportMode by remember { mutableStateOf(ExportMode.DETAILED) }

    val dateRangePickerState = rememberDateRangePickerState()
    var showCustomPicker by remember { mutableStateOf(false) }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = Color.Black
        ) {
            Box(modifier = Modifier.fillMaxSize()) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .systemBarsPadding()
                ) {
                    // Header
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 8.dp, vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(onClick = onDismiss) {
                            Icon(Icons.Default.ArrowBack, contentDescription = "Indietro", tint = Color.White)
                        }
                    }

                    // Titolo
                    Text(
                        text = "Esporta PDF",
                        style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White,
                        modifier = Modifier.padding(horizontal = 24.dp, vertical = 8.dp)
                    )

                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f)
                            .verticalScroll(rememberScrollState())
                            .padding(horizontal = 24.dp, vertical = 16.dp),
                        verticalArrangement = Arrangement.spacedBy(24.dp)
                    ) {
                        // 0. SCELTA LAYOUT
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFF1C1C1E), RoundedCornerShape(14.dp))
                                .padding(16.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Text(
                                "Layout Report",
                                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                                color = Color.White
                            )
                            Column {
                                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                    RadioButton(
                                        selected = selectedExportMode == ExportMode.DETAILED,
                                        onClick = { selectedExportMode = ExportMode.DETAILED },
                                        colors = RadioButtonDefaults.colors(selectedColor = MaterialTheme.colorScheme.primary, unselectedColor = Color.White)
                                    )
                                    Text("Dettagliato (Tutti i movimenti)", style = MaterialTheme.typography.bodyMedium, color = Color.White)
                                }
                                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                    RadioButton(
                                        selected = selectedExportMode == ExportMode.COMPACT_CATEGORY,
                                        onClick = { selectedExportMode = ExportMode.COMPACT_CATEGORY },
                                        colors = RadioButtonDefaults.colors(selectedColor = MaterialTheme.colorScheme.primary, unselectedColor = Color.White)
                                    )
                                    Text("Compatto (Totali per categoria)", style = MaterialTheme.typography.bodyMedium, color = Color.White)
                                }
                                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                    RadioButton(
                                        selected = selectedExportMode == ExportMode.BALANCE_ONLY,
                                        onClick = { selectedExportMode = ExportMode.BALANCE_ONLY },
                                        colors = RadioButtonDefaults.colors(selectedColor = MaterialTheme.colorScheme.primary, unselectedColor = Color.White)
                                    )
                                    Text("Solo Saldo Attuale", style = MaterialTheme.typography.bodyMedium, color = Color.White)
                                }
                            }
                        }

                        if (selectedExportMode != ExportMode.BALANCE_ONLY) {
                            // FILTRI
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(Color(0xFF1C1C1E), RoundedCornerShape(14.dp))
                                    .padding(16.dp),
                                verticalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                Text(
                                    "Filtri",
                                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                                    color = Color.White
                                )

                                // 1. FILTRO PERIODO
                                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                    Text("Periodo", style = MaterialTheme.typography.labelMedium, color = Color.Gray)
                                    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                        listOf("OGGI", "MESE", "ANNO", "CUSTOM").forEach { option ->
                                            FilterChip(
                                                selected = selectedPeriod == option,
                                                onClick = {
                                                    selectedPeriod = option
                                                    if (option == "CUSTOM") showCustomPicker = true
                                                },
                                                label = { Text(if (option == "CUSTOM") "Date..." else option.lowercase().replaceFirstChar { it.uppercase() }) },
                                                colors = FilterChipDefaults.filterChipColors(
                                                    containerColor = Color.Transparent,
                                                    labelColor = Color.White,
                                                    selectedContainerColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.2f),
                                                    selectedLabelColor = MaterialTheme.colorScheme.primary
                                                )
                                            )
                                        }
                                    }

                                    if (selectedPeriod == "MESE") {
                                        Row(
                                            modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                                            horizontalArrangement = Arrangement.Center,
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            IconButton(onClick = {
                                                if (targetMonth == 1) { targetMonth = 12; targetYear-- } else { targetMonth-- }
                                            }) { Icon(Icons.Rounded.ChevronLeft, "Mese precedente", tint = MaterialTheme.colorScheme.primary) }

                                            Text(
                                                text = "${mesiItaliani[targetMonth - 1]} $targetYear",
                                                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                                                modifier = Modifier.padding(horizontal = 16.dp),
                                                color = Color.White
                                            )

                                            IconButton(onClick = {
                                                if (targetMonth == 12) { targetMonth = 1; targetYear++ } else { targetMonth++ }
                                            }) { Icon(Icons.Rounded.ChevronRight, "Mese successivo", tint = MaterialTheme.colorScheme.primary) }
                                        }
                                    }

                                    if (selectedPeriod == "CUSTOM" && dateRangePickerState.selectedStartDateMillis != null) {
                                        val start = Instant.fromEpochMilliseconds(dateRangePickerState.selectedStartDateMillis!!).toLocalDateTime(TimeZone.UTC).date
                                        val end = dateRangePickerState.selectedEndDateMillis?.let { Instant.fromEpochMilliseconds(it).toLocalDateTime(TimeZone.UTC).date } ?: start
                                        Text("Dal: $start al $end", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                                    }
                                }

                                // 2. FILTRO TIPO MOVIMENTO
                                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                    Text("Tipo di movimenti", style = MaterialTheme.typography.labelMedium, color = Color.Gray)
                                    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                        listOf("TUTTO", "ENTRATE", "USCITE").forEach { option ->
                                            FilterChip(
                                                selected = selectedType == option,
                                                onClick = { selectedType = option },
                                                label = { Text(option.lowercase().replaceFirstChar { it.uppercase() }) },
                                                colors = FilterChipDefaults.filterChipColors(
                                                    containerColor = Color.Transparent,
                                                    labelColor = Color.White,
                                                    selectedContainerColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.2f),
                                                    selectedLabelColor = MaterialTheme.colorScheme.primary
                                                )
                                            )
                                        }
                                    }
                                }

                                // 3. FILTRO CONTO
                                var accExpanded by remember { mutableStateOf(false) }
                                ExposedDropdownMenuBox(
                                    expanded = accExpanded,
                                    onExpandedChange = { accExpanded = !accExpanded }
                                ) {
                                    MonetixTextField(
                                        value = accounts.find { it.id == selectedAccountId }?.name ?: "Tutti i conti",
                                        onValueChange = {},
                                        readOnly = true,
                                        label = { Text("Conto") },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = accExpanded) },
                                        modifier = Modifier.menuAnchor().fillMaxWidth()
                                    )
                                    ExposedDropdownMenu(
                                        expanded = accExpanded,
                                        onDismissRequest = { accExpanded = false },
                                        modifier = Modifier.background(Color(0xFF1C1C1E))
                                    ) {
                                        DropdownMenuItem(
                                            text = { Text("Tutti i conti", fontWeight = FontWeight.Bold, color = Color.White) },
                                            onClick = { selectedAccountId = null; accExpanded = false }
                                        )
                                        accounts.filter { !it.isDeleted }.forEach { acc ->
                                            DropdownMenuItem(
                                                text = { Text(acc.name, color = Color.White) },
                                                onClick = { selectedAccountId = acc.id; accExpanded = false }
                                            )
                                        }
                                    }
                                }

                                // 4. FILTRO CATEGORIA
                                var catExpanded by remember { mutableStateOf(false) }
                                ExposedDropdownMenuBox(
                                    expanded = catExpanded,
                                    onExpandedChange = { catExpanded = !catExpanded }
                                ) {
                                    MonetixTextField(
                                        value = selectedCategory?.name ?: "Tutte le categorie",
                                        onValueChange = {},
                                        readOnly = true,
                                        label = { Text("Categoria") },
                                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = catExpanded) },
                                        modifier = Modifier.menuAnchor().fillMaxWidth()
                                    )
                                    ExposedDropdownMenu(
                                        expanded = catExpanded,
                                        onDismissRequest = { catExpanded = false },
                                        modifier = Modifier.background(Color(0xFF1C1C1E))
                                    ) {
                                        DropdownMenuItem(
                                            text = { Text("Tutte le categorie", fontWeight = FontWeight.Bold, color = Color.White) },
                                            onClick = { selectedCategory = null; catExpanded = false }
                                        )
                                        categories.filter { !it.isDeleted }.forEach { cat ->
                                            DropdownMenuItem(
                                                text = { Text(cat.name, color = Color.White) },
                                                onClick = { selectedCategory = cat; catExpanded = false }
                                            )
                                        }
                                    }
                                }

                                // 5. FILTRO ESERCENTE
                                MonetixTextField(
                                    value = merchantQuery, onValueChange = { merchantQuery = it },
                                    label = { Text("Esercente (opzionale)") }, modifier = Modifier.fillMaxWidth(), singleLine = true,
                                    trailingIcon = { if (merchantQuery.isNotEmpty()) IconButton(onClick = { merchantQuery = "" }) { Icon(Icons.Default.Close, null, tint = Color.White) } }
                                )

                                // 6. FILTRO NOTA
                                MonetixTextField(
                                    value = noteQuery, onValueChange = { noteQuery = it },
                                    label = { Text("Nota o Descrizione (opzionale)") }, modifier = Modifier.fillMaxWidth(), singleLine = true,
                                    trailingIcon = { if (noteQuery.isNotEmpty()) IconButton(onClick = { noteQuery = "" }) { Icon(Icons.Default.Close, null, tint = Color.White) } }
                                )
                            }
                        }

                        Spacer(modifier = Modifier.height(80.dp))
                    }
                }

                // FAB In basso a destra
                FloatingActionButton(
                    onClick = {
                        val todayDate = Clock.System.todayIn(TimeZone.currentSystemDefault())

                        if (selectedExportMode == ExportMode.BALANCE_ONLY) {
                            exporter.exportAndSharePdf(emptyList(), "Report Saldo Attuale", 0L, emptyMap(), availableBalanceCents, selectedExportMode)
                            onDismiss()
                            return@FloatingActionButton
                        }

                        // 1. TROVIAMO I PADRI CHE PASSANO "NATURALMENTE" I FILTRI
                        val validIdsInitially = transactions.filter { tx ->
                            val isHiddenOk = if (!includeHidden) !tx.isHidden else true

                            val txDate = LocalDate.fromEpochDays(tx.dateEpochDay.toInt())
                            val isDateOk = when (selectedPeriod) {
                                "OGGI" -> tx.dateEpochDay == todayDate.toEpochDays().toLong()
                                "MESE" -> txDate.monthNumber == targetMonth && txDate.year == targetYear
                                "ANNO" -> txDate.year == todayDate.year
                                "CUSTOM" -> {
                                    val startMillis = dateRangePickerState.selectedStartDateMillis ?: 0L
                                    val endMillis = dateRangePickerState.selectedEndDateMillis ?: startMillis
                                    val startEpoch = Instant.fromEpochMilliseconds(startMillis).toLocalDateTime(TimeZone.UTC).date.toEpochDays().toLong()
                                    val endEpoch = Instant.fromEpochMilliseconds(endMillis).toLocalDateTime(TimeZone.UTC).date.toEpochDays().toLong()
                                    tx.dateEpochDay in startEpoch..endEpoch
                                }
                                else -> true
                            }

                            val isTypeOk = when (selectedType) { "ENTRATE" -> tx.type == "INCOME"; "USCITE" -> tx.type == "EXPENSE"; else -> true }
                            val isCatOk = if (selectedCategory != null) tx.categoryId == selectedCategory?.id else true
                            val isMerchantOk = if (merchantQuery.isNotBlank()) tx.merchantKey?.contains(merchantQuery, ignoreCase = true) == true else true
                            val isNoteOk = if (noteQuery.isNotBlank()) tx.description?.contains(noteQuery, ignoreCase = true) == true else true

                            val isAccOk = if (selectedAccountId != null) {
                                val ghostId = "sys_ghost_$selectedAccountId"
                                tx.accountId == selectedAccountId || tx.fromAccountId == selectedAccountId || tx.toAccountId == selectedAccountId ||
                                        tx.accountId == ghostId || tx.fromAccountId == ghostId || tx.toAccountId == ghostId
                            } else true

                            isHiddenOk && isDateOk && isTypeOk && isCatOk && isMerchantOk && isNoteOk && isAccOk
                        }.map { it.id }.toSet()

                        // 2. UNIAMO I PADRI VALIDI E PESCHIAMO I LORO FIGLI OVUNQUE SI TROVINO NEL TEMPO!
                        val filteredTxs = transactions.filter { tx ->
                            val isChild = tx.description?.contains("[cash:") == true || tx.merchantKey?.contains("[cash:") == true
                            if (isChild) {
                                val parentId = Regex("\\[cash:([^\\]]+)\\]").find(tx.description ?: tx.merchantKey ?: "")?.groupValues?.get(1)
                                parentId != null && parentId in validIdsInitially
                            } else {
                                tx.id in validIdsInitially
                            }
                        }

                        // 3. MOTORE DI RAGGRUPPAMENTO PADRE-FIGLIO PER LA STAMPA
                        val finalOrderedTxs = mutableListOf<TransactionEntity>()
                        val processedIds = mutableSetOf<String>()
                        val sortedTxs = filteredTxs.sortedByDescending { it.dateEpochDay }

                        for (tx in sortedTxs) {
                            if (tx.id in processedIds) continue

                            val isChild = tx.description?.contains("[cash:") == true || tx.merchantKey?.contains("[cash:") == true

                            if (!isChild) {
                                finalOrderedTxs.add(tx)
                                processedIds.add(tx.id)

                                val children = sortedTxs.filter { child ->
                                    !processedIds.contains(child.id) &&
                                            (child.description?.contains("[cash:${tx.id}]") == true || child.merchantKey?.contains("[cash:${tx.id}]") == true)
                                }

                                children.forEach { child ->
                                    val childName = (child.merchantKey ?: child.description ?: "").replace(Regex("\\[.*?\\]"), "").trim()
                                    finalOrderedTxs.add(child.copy(merchantKey = "   ↳ Di cui: $childName"))
                                    processedIds.add(child.id)
                                }
                            }
                        }

                        // Titoli per il PDF
                        val periodTitle = when(selectedPeriod) {
                            "OGGI" -> "Giornaliero ($todayDate)"
                            "MESE" -> "Mensile (${mesiItaliani[targetMonth - 1]} $targetYear)"
                            "ANNO" -> "Annuale (${todayDate.year})"
                            else -> "Personalizzato"
                        }
                        val typeTitle = if (selectedType != "TUTTO") " - Solo $selectedType" else ""
                        val catTitle = if (selectedCategory != null) " - Categoria: ${selectedCategory?.name}" else ""
                        val accTitle = if (selectedAccountId != null) " - Conto: ${accounts.find{it.id == selectedAccountId}?.name}" else ""

                        val title = "Report $periodTitle$typeTitle$catTitle$accTitle"

                        val totalCents = finalOrderedTxs.sumOf { tx ->
                            val isChild = tx.description?.contains("[cash:") == true || tx.merchantKey?.contains("[cash:") == true
                            if (isChild) 0L
                            else if (tx.type == "EXPENSE") -tx.amountCents
                            else if (tx.type == "INCOME") tx.amountCents
                            else 0L
                        }

                        val catNamesMap = categories.associate { it.id to it.name }

                        exporter.exportAndSharePdf(finalOrderedTxs, title, totalCents, catNamesMap, availableBalanceCents, selectedExportMode)
                        onDismiss()
                    },
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                        .padding(24.dp),
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = Color.White,
                    shape = RoundedCornerShape(16.dp)
                ) {
                    Icon(Icons.Default.Check, contentDescription = "Esporta PDF")
                }
            }
        }
    }

    if (showCustomPicker) {
        DatePickerDialog(
            onDismissRequest = {
                showCustomPicker = false
                if(dateRangePickerState.selectedStartDateMillis == null) selectedPeriod = "MESE"
            },
            confirmButton = { TextButton(onClick = { showCustomPicker = false }) { Text("CONFERMA") } }
        ) {
            DateRangePicker(state = dateRangePickerState, modifier = Modifier.weight(1f))
        }
    }
}
